// Karte einer Opportunity im Pipeline-Board: Lead-Name, darunter Zuständiger, Konfidenz, Abschlussdatum (und Wert). Zwei Zeilen,
// weitere nur nach Wahl unter „Optionen“ (Lead-Status, Kontakt, Notiz). Ziehen legt sie in eine andere Spalte, das Menü
// „…“ erreicht dasselbe ohne Maus. Ein Klick auf die Karte öffnet den Lead.
import type { DragEvent } from "react";
import { Link } from "react-router-dom";
import type { OpportunityStatus } from "../api";
import type { OpportunityZeile } from "../api/opportunities";
import { Avatar, StatusPille } from "../components/Bausteine";
import { Icon } from "../components/Icon";
import { MenueKnopf } from "../components/Menue";
import { datumKurz, wertText } from "./logik";
import { opportunityMenue } from "./menue";

export interface KartenOptionen {
  leadStatus: boolean;
  kontakt: boolean;
  notiz: boolean;
}

export const STANDARD_KARTENOPTIONEN: KartenOptionen = { leadStatus: false, kontakt: false, notiz: false };

interface Props {
  opp: OpportunityZeile;
  statuses: OpportunityStatus[];
  optionen: KartenOptionen;
  darfAendern: boolean;
  zieht: boolean;
  onZiehStart: (e: DragEvent<HTMLElement>, opp: OpportunityZeile) => void;
  onZiehEnde: () => void;
  onOeffnen: (opp: OpportunityZeile) => void;
  onBearbeiten: (opp: OpportunityZeile) => void;
  onStatus: (opp: OpportunityZeile, statusId: string) => void;
}

export function Karte({ opp, statuses, optionen, darfAendern, zieht, onZiehStart, onZiehEnde, onOeffnen, onBearbeiten, onStatus }: Props) {
  const datum = datumKurz(opp.status_type === "lost" ? opp.date_lost : opp.date_won);
  const wert = wertText(opp.value, opp.value_period);
  const notiz = (opp.note ?? "").trim().split("\n")[0];
  const name = opp.lead_name ?? "Lead ohne Namen";
  return (
    <div
      className={`opp-karte${zieht ? " opp-karte--zieht" : ""}${darfAendern ? "" : " opp-karte--fest"}`}
      draggable={darfAendern}
      onDragStart={(e) => onZiehStart(e, opp)}
      onDragEnd={onZiehEnde}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button, a, [role='menu']")) return;
        onOeffnen(opp);
      }}
      data-id={opp.id}
    >
      <div className="opp-karte__kopf">
        <Link to={`/lead/${encodeURIComponent(opp.lead_id)}`} className="opp-karte__lead" draggable={false} title={name}>
          {name}
        </Link>
        <span className="opp-karte__aktionen">
          <button
            type="button"
            className="symbolknopf symbolknopf--klein"
            title={darfAendern ? "Opportunity bearbeiten" : "Fremde Opportunity: keine Berechtigung"}
            aria-label="Opportunity bearbeiten"
            disabled={!darfAendern}
            onClick={() => onBearbeiten(opp)}
          >
            <Icon name="stift" groesse={14} />
          </button>
          <MenueKnopf
            knopfKlasse="symbolknopf symbolknopf--klein"
            titel="Mehr"
            ausrichtung="end"
            breite={220}
            punkte={opportunityMenue({
              opp,
              statuses,
              darfAendern,
              onOeffnen: () => onOeffnen(opp),
              onBearbeiten: () => onBearbeiten(opp),
              onStatus: (id) => onStatus(opp, id),
            })}
          >
            <Icon name="mehr" groesse={14} />
          </MenueKnopf>
        </span>
      </div>
      <div className="opp-karte__zeile">
        <Avatar name={opp.user_name} id={opp.user_id} groesse={18} titel={opp.user_name ?? "Niemand zuständig"} />
        {opp.confidence !== null && opp.confidence !== undefined ? <span className="opp-karte__konfidenz">{opp.confidence} %</span> : null}
        {datum ? <span className="opp-karte__datum">{datum}</span> : null}
        {wert ? <span className="opp-karte__wert">{wert}</span> : null}
      </div>
      {optionen.leadStatus && opp.lead_status_label ? (
        <div className="opp-karte__extra">
          <StatusPille klein label={opp.lead_status_label} farbe="gray" />
        </div>
      ) : null}
      {optionen.kontakt && opp.contact_name ? (
        <div className="opp-karte__extra opp-karte__extra--text">
          <Icon name="person" groesse={13} />
          <span>{opp.contact_name}</span>
        </div>
      ) : null}
      {optionen.notiz && notiz ? <div className="opp-karte__extra opp-karte__extra--text opp-karte__notiz" title={opp.note ?? undefined}>{notiz}</div> : null}
    </div>
  );
}
